"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Flame as FlameOff, Gauge, ShieldAlert, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const problemList = [{
  id: "clogged-drains",
  title: "Clogged Drains",
  description: "Slow-draining sinks, standing tub water, and stubborn grease or hair blockages.",
  icon: Droplets,
  severity: "Common Issue"
}, {
  id: "water-leaks",
  title: "Water Leaks",
  description: "Hidden slab leaks, dripping fixtures, and wall moisture spots requiring rapid pinpointing.",
  icon: Droplets,
  severity: "High Priority"
}, {
  id: "no-hot-water",
  title: "No Hot Water",
  description: "Sudden temperature drops, faulty heating elements, or gas valve failures.",
  icon: FlameOff,
  severity: "Urgent Repair"
}, {
  id: "low-water-pressure",
  title: "Low Water Pressure",
  description: "Sudden pressure loss, aerator buildup, or hidden distribution line restrictions.",
  icon: Gauge,
  severity: "Diagnostic"
}, {
  id: "sewer-backups",
  title: "Sewer Backups",
  description: "Dangerous line backups, sewer gas odors, and main lateral line stoppages.",
  icon: AlertTriangle,
  severity: "Hazard Warning"
}, {
  id: "toilet-problems",
  title: "Toilet Problems",
  description: "Constant running, bowl overflows, broken flappers, and total mechanical failure.",
  icon: Wrench,
  severity: "Same-Day Fix"
}, {
  id: "burst-pipes",
  title: "Burst Pipes",
  description: "Ruptured supply lines and catastrophic indoor flooding requiring instant shut-off.",
  icon: ShieldAlert,
  severity: "Immediate Emergency"
}, {
  id: "water-heater-problems",
  title: "Water Heater Problems",
  description: "Leaking tanks, sediment popping sounds, pilot light issues, or rusted fittings.",
  icon: Flame,
  severity: "Critical System"
}];
export default function Problems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="problemswehandle" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold border-primary/20 text-primary bg-primary/5">
              <Zap className="size-3 text-accent" />
              Immediate Triage
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Plumbing Problems We Help With
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            From sudden line ruptures to persistent drainage problems in Jonesboro, our emergency team resolves critical household plumbing failures day or night.
          </p>
        </div>

        {/* 4-Column Diagnostic Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {problemList.map((problem, index) => {
          const IconComponent = problem.icon;
          return <motion.div key={problem.id} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border/80 bg-card hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] rounded-xl overflow-hidden group">
                  <CardContent className="p-6 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-[background-color,color] duration-200 shrink-0">
                          <IconComponent className="size-6" />
                        </div>
                        <span className="text-[11px] font-mono font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md border border-border/60">
                          {problem.severity}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-card-foreground group-hover:text-primary transition-colors font-default mb-2">
                        {problem.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-normal">
                        {problem.description}
                      </p>
                    </div>

                    <div className="pt-5 mt-4 border-t border-border/60 flex items-center justify-between text-xs font-semibold text-primary">
                      <span>Dispatch diagnosis ready</span>
                      <ArrowRight className="size-3.5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Section Action / Footer Link */}
        <div className="mt-12 md:mt-16 text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm px-6 h-12 text-base font-medium rounded-lg">
            <Link to="/problems/" className="inline-flex items-center gap-2">
              <span>View All Plumbing Problems</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-12 px-6 rounded-lg border-border hover:bg-muted text-foreground">
            <a href="tel:4708479076" className="inline-flex items-center gap-2">
              <span>Call 24/7 Dispatch Hotline</span>
            </a>
          </Button>
        </div>
      </div>
    </section>;
}